import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Activity, Calendar, ShieldAlert } from "lucide-react";
import { buildHypertensionFormStyles } from "./hypertension/styleHelpers";
import {
  HYPERTENSION_STAGES,
  HYPERTENSION_SERVICE_NAME,
  getStage,
} from "@/lib/hypertensionCaseFinding";
import { links } from "@/lib/linksRegistry";
import { ClinicalReferencesCollapsible } from "./ClinicalReferencesCollapsible";

export interface NHSHypertensionABPMFittingFormData {
  patientConsent: "yes" | "no" | "";
  consultationDate: string;
  consultationTime: string;

  // Pre-fitting checks
  previousStageReviewed: boolean;
  contraindicationsChecked: boolean;
  contraindicationDetails: string;

  // Device
  deviceMakeModel: string;
  deviceSerialNumber: string;
  cuffSize: "small" | "standard" | "large" | "";
  armUsed: "left" | "right" | "";
  testReadingSystolic: string;
  testReadingDiastolic: string;

  // Schedule / readings programme
  monitoringStart: string; // datetime-local
  monitoringEnd: string; // datetime-local
  daytimeFrequency: string;
  nighttimeFrequency: string;

  // Patient education
  diaryProvided: boolean;
  removalInstructionsGiven: boolean;
  emergencyContactGiven: boolean;
  patientUnderstandsCare: "yes" | "no" | "";

  // Follow-up
  followUpDate: string;
  followUpNotes: string;

  internalNotes: string;
}

interface Props {
  isDarkMode?: boolean;
  onSave?: (data: NHSHypertensionABPMFittingFormData) => void;
  onComplete?: (data: NHSHypertensionABPMFittingFormData) => void;
  initialData?: Partial<NHSHypertensionABPMFittingFormData>;
  nhsHypertensionABPMFittingFormLoading?: boolean;
  consultationStatus?: number;
}

const defaultData: NHSHypertensionABPMFittingFormData = {
  patientConsent: "",
  consultationDate: new Date().toISOString().split("T")[0],
  consultationTime: new Date().toTimeString().slice(0, 5),
  previousStageReviewed: false,
  contraindicationsChecked: false,
  contraindicationDetails: "",
  deviceMakeModel: "",
  deviceSerialNumber: "",
  cuffSize: "",
  armUsed: "",
  testReadingSystolic: "",
  testReadingDiastolic: "",
  monitoringStart: "",
  monitoringEnd: "",
  daytimeFrequency: "30",
  nighttimeFrequency: "60",
  diaryProvided: false,
  removalInstructionsGiven: false,
  emergencyContactGiven: false,
  patientUnderstandsCare: "",
  followUpDate: "",
  followUpNotes: "",
  internalNotes: "",
};

export function NHSHypertensionABPMFittingForm({
  isDarkMode = false,
  onSave,
  onComplete,
  initialData,
  nhsHypertensionABPMFittingFormLoading = false,
  consultationStatus,
}: Props) {
  const [formData, setFormData] = useState<NHSHypertensionABPMFittingFormData>({
    ...defaultData,
    ...initialData,
  });
  const [clinicalRefsOpen, setClinicalRefsOpen] = useState(false);

  const stage = getStage("nhs-htn-abpm-fitting");
  const clinicalLinkRefs = stage.clinicalLinkRefs || [];
  const headerLinks = clinicalLinkRefs.map((ref) => links[ref]).filter(Boolean);

  const handleChange = <K extends keyof NHSHypertensionABPMFittingFormData>(
    field: K,
    value: NHSHypertensionABPMFittingFormData[K],
  ) => setFormData((prev) => ({ ...prev, [field]: value }));

  const styles = buildHypertensionFormStyles(isDarkMode);
  const {
    card,
    sectionBadge,
    sectionTitle,
    labelCls,
    inputCls,
    selectCls,
    selectContentCls,
    radioCls,
    radioLabelCls,
    helperText,
  } = styles;

  const canSubmit =
    formData.patientConsent === "yes" &&
    formData.previousStageReviewed &&
    formData.contraindicationsChecked &&
    formData.deviceMakeModel.trim() !== "" &&
    formData.cuffSize !== "" &&
    formData.armUsed !== "" &&
    formData.monitoringStart !== "" &&
    formData.monitoringEnd !== "" &&
    formData.patientUnderstandsCare === "yes" &&
    formData.followUpDate !== "";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSave) onSave(formData);
    if (onComplete) onComplete(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Service header */}
      <div
        className={`z-10 rounded-2xl p-4 shadow-lg ${
          isDarkMode
            ? "bg-rose-900/20 border border-rose-800"
            : "bg-rose-50 border border-rose-200"
        }`}
        style={{
          backgroundImage: isDarkMode
            ? "linear-gradient(180deg, rgba(159,18,57,0.3) 0%, rgba(159,18,57,0.2) 100%)"
            : "linear-gradient(180deg, rgba(254,240,242,1) 0%, rgba(254,240,242,1) 80%, rgba(254,240,242,0.98) 100%)",
        }}
      >
        <div className="flex items-start gap-3">
          <Activity
            className={`w-5 h-5 mt-0.5 ${
              isDarkMode ? "text-rose-300" : "text-rose-600"
            }`}
          />
          <div className="flex-1">
            <div
              className={`text-sm font-semibold ${
                isDarkMode ? "text-rose-200" : "text-rose-900"
              }`}
            >
              {HYPERTENSION_SERVICE_NAME} – Stage 2: ABPM Fitting
            </div>
            <p className={`mt-1 text-xs ${helperText}`}>
              Fit the ambulatory blood pressure monitor, programme the device
              and brief the patient on care, daily diary and removal.
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
              {HYPERTENSION_STAGES.map((s) => (
                <span
                  key={s.id}
                  className={`px-2 py-0.5 rounded-full border ${
                    s.number === 2
                      ? isDarkMode
                        ? "bg-rose-800 border-rose-700 text-rose-100"
                        : "bg-rose-600 border-rose-600 text-white"
                      : isDarkMode
                        ? "border-gray-700 text-gray-400"
                        : "border-gray-300 text-gray-500"
                  }`}
                >
                  Stage {s.number}: {s.shortLabel}
                </span>
              ))}
            </div>
            {headerLinks.length > 0 && (
              <ClinicalReferencesCollapsible
                links={headerLinks}
                isDarkMode={isDarkMode}
                isOpen={clinicalRefsOpen}
                onOpenChange={setClinicalRefsOpen}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Section 1: Consent ───────────────────────────────────────────── */}
      <div className={card}>
        <div className="flex items-center gap-3 mb-4">
          <div className={sectionBadge}>1</div>
          <h3 className={sectionTitle}>Patient Consent</h3>
        </div>
        <RadioGroup
          value={formData.patientConsent}
          onValueChange={(v) => handleChange("patientConsent", v as any)}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem
              value="yes"
              id="abpmf-consent-yes"
              className={radioCls}
            />
            <label htmlFor="abpmf-consent-yes" className={radioLabelCls}>
              Yes
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem
              value="no"
              id="abpmf-consent-no"
              className={radioCls}
            />
            <label htmlFor="abpmf-consent-no" className={radioLabelCls}>
              No
            </label>
          </div>
        </RadioGroup>
      </div>

      {formData.patientConsent === "yes" && (
        <>
          {/* ── Section 2: Date / Pre-Fitting Checks ─────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>2</div>
              <h3 className={sectionTitle}>Pre-Fitting Checks</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelCls}>Date</Label>
                <div className="relative">
                  <Input
                    type="date"
                    value={formData.consultationDate}
                    onChange={(e) =>
                      handleChange("consultationDate", e.target.value)
                    }
                    className={inputCls}
                  />
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Time</Label>
                <Input
                  type="time"
                  value={formData.consultationTime}
                  onChange={(e) =>
                    handleChange("consultationTime", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
            </div>

            <div className="space-y-3 mt-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="prev-stage"
                  checked={formData.previousStageReviewed}
                  onCheckedChange={(v) =>
                    handleChange("previousStageReviewed", Boolean(v))
                  }
                />
                <label htmlFor="prev-stage" className={radioLabelCls}>
                  Stage 1 (Initial Consultation) record reviewed
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="contra"
                  checked={formData.contraindicationsChecked}
                  onCheckedChange={(v) =>
                    handleChange("contraindicationsChecked", Boolean(v))
                  }
                />
                <label htmlFor="contra" className={radioLabelCls}>
                  Contraindications to ABPM checked (e.g. AF, lymphoedema, arm
                  injury)
                </label>
              </div>
              {formData.contraindicationsChecked && (
                <div className="space-y-2">
                  <Label className={labelCls}>
                    Any contraindications identified? (notes)
                  </Label>
                  <Textarea
                    rows={2}
                    value={formData.contraindicationDetails}
                    onChange={(e) =>
                      handleChange("contraindicationDetails", e.target.value)
                    }
                    className={inputCls}
                  />
                </div>
              )}
            </div>
          </div>

          {/* ── Section 3: Device & Programming ──────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>3</div>
              <h3 className={sectionTitle}>Device &amp; Programming</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelCls}>Device make / model</Label>
                <Input
                  placeholder="e.g. Microlife WatchBP O3"
                  value={formData.deviceMakeModel}
                  onChange={(e) =>
                    handleChange("deviceMakeModel", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Serial number</Label>
                <Input
                  value={formData.deviceSerialNumber}
                  onChange={(e) =>
                    handleChange("deviceSerialNumber", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Cuff size</Label>
                <Select
                  value={formData.cuffSize}
                  onValueChange={(v) => handleChange("cuffSize", v as any)}
                >
                  <SelectTrigger className={selectCls}>
                    <SelectValue placeholder="Select cuff size" />
                  </SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    <SelectItem value="small">Small</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="large">Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Arm used</Label>
                <Select
                  value={formData.armUsed}
                  onValueChange={(v) => handleChange("armUsed", v as any)}
                >
                  <SelectTrigger className={selectCls}>
                    <SelectValue placeholder="Select arm" />
                  </SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>
                  Test reading – Systolic (mmHg)
                </Label>
                <Input
                  type="number"
                  value={formData.testReadingSystolic}
                  onChange={(e) =>
                    handleChange("testReadingSystolic", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>
                  Test reading – Diastolic (mmHg)
                </Label>
                <Input
                  type="number"
                  value={formData.testReadingDiastolic}
                  onChange={(e) =>
                    handleChange("testReadingDiastolic", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Monitoring start</Label>
                <Input
                  type="datetime-local"
                  value={formData.monitoringStart}
                  onChange={(e) =>
                    handleChange("monitoringStart", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Monitoring end</Label>
                <Input
                  type="datetime-local"
                  value={formData.monitoringEnd}
                  onChange={(e) =>
                    handleChange("monitoringEnd", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Daytime frequency (minutes)</Label>
                <Input
                  type="number"
                  value={formData.daytimeFrequency}
                  onChange={(e) =>
                    handleChange("daytimeFrequency", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>
                  Night-time frequency (minutes)
                </Label>
                <Input
                  type="number"
                  value={formData.nighttimeFrequency}
                  onChange={(e) =>
                    handleChange("nighttimeFrequency", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
            </div>
          </div>

          {/* ── Section 4: Patient Education ───────────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>4</div>
              <h3 className={sectionTitle}>Patient Education</h3>
            </div>
            <Alert
              className={
                isDarkMode
                  ? "bg-amber-900/20 border-amber-700 text-amber-200 mb-4"
                  : "bg-amber-50 border-amber-300 text-amber-900 mb-4"
              }
            >
              <ShieldAlert className="h-4 w-4" />
              <AlertDescription className="text-sm">
                Confirm the patient understands how to wear the monitor, when to
                remove it, and how to record activities in their diary.
              </AlertDescription>
            </Alert>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="diary"
                  checked={formData.diaryProvided}
                  onCheckedChange={(v) =>
                    handleChange("diaryProvided", Boolean(v))
                  }
                />
                <label htmlFor="diary" className={radioLabelCls}>
                  Diary card / app instructions provided
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="removal"
                  checked={formData.removalInstructionsGiven}
                  onCheckedChange={(v) =>
                    handleChange("removalInstructionsGiven", Boolean(v))
                  }
                />
                <label htmlFor="removal" className={radioLabelCls}>
                  Removal instructions explained
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="emergency"
                  checked={formData.emergencyContactGiven}
                  onCheckedChange={(v) =>
                    handleChange("emergencyContactGiven", Boolean(v))
                  }
                />
                <label htmlFor="emergency" className={radioLabelCls}>
                  Emergency contact / pharmacy phone number given
                </label>
              </div>

              <div className="space-y-2 pt-2">
                <Label className={labelCls}>
                  Patient confirms they understand how to manage the device
                </Label>
                <RadioGroup
                  value={formData.patientUnderstandsCare}
                  onValueChange={(v) =>
                    handleChange("patientUnderstandsCare", v as any)
                  }
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="yes"
                      id="understand-yes"
                      className={radioCls}
                    />
                    <label htmlFor="understand-yes" className={radioLabelCls}>
                      Yes
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="no"
                      id="understand-no"
                      className={radioCls}
                    />
                    <label htmlFor="understand-no" className={radioLabelCls}>
                      No
                    </label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </div>

          {/* ── Section 5: Follow-up appointment ───────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>5</div>
              <h3 className={sectionTitle}>Stage 3 Follow-Up</h3>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className={labelCls}>Follow-up appointment date</Label>
                <Input
                  type="date"
                  value={formData.followUpDate}
                  onChange={(e) => handleChange("followUpDate", e.target.value)}
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Follow-up notes</Label>
                <Textarea
                  rows={2}
                  value={formData.followUpNotes}
                  onChange={(e) =>
                    handleChange("followUpNotes", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
            </div>
          </div>

          <div className={card}>
            <div className="space-y-2">
              <Label className={labelCls}>Internal notes</Label>
              <Textarea
                rows={3}
                value={formData.internalNotes}
                onChange={(e) => handleChange("internalNotes", e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="submit"
          disabled={
            nhsHypertensionABPMFittingFormLoading ||
            consultationStatus === 1 ||
            !canSubmit
          }
        >
          {nhsHypertensionABPMFittingFormLoading
            ? "Please wait..."
            : "Complete Stage 2"}
        </Button>
      </div>
    </form>
  );
}
